/**
 * Reader page.
 *
 * Two modes:
 *   - streaming (?streaming=1): open the SSE stream and render pages live.
 *   - re-read: fetch the materialised book (served from cache when offline).
 *
 * After reading, the child can start the comprehension quiz.
 */

import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import { useSession } from "@/app/useSession";
import { BookReader } from "@/features/book/BookReader";
import type { StreamingPage } from "@/features/book/useBookStream";
import { QuizFlow } from "@/features/qa/QuizFlow";
import { books as booksApi, type Book, type Question } from "@/lib/api";

function toStreamingPages(book: Book): StreamingPage[] {
  return book.pages.map((page) => ({
    pageNumber: page.pageNumber,
    text: page.text,
    aspectRatio:
      page.imageWidth && page.imageHeight
        ? page.imageWidth / page.imageHeight
        : 1,
    image: page.imageUrl
      ? { url: page.imageUrl, width: page.imageWidth, height: page.imageHeight }
      : null,
    textComplete: true,
    // Unknown (older cache without the field) → only expect an image if one is
    // present, so legacy text-only pages stay clean rather than showing a slot.
    expectsImage: page.expectsImage ?? page.imageUrl != null,
    imageFailed: false,
  }));
}

export function ReaderPage() {
  const { bookId = "" } = useParams();
  const [params] = useSearchParams();
  const streaming = params.get("streaming") === "1";
  const { activeChild } = useSession();
  const navigate = useNavigate();

  const [book, setBook] = useState<Book | null>(null);
  const [questions, setQuestions] = useState<Question[] | null>(null);
  const [showQuiz, setShowQuiz] = useState(false);

  // In re-read mode, load the materialised book.
  useEffect(() => {
    if (streaming || !bookId) return;
    let active = true;
    booksApi
      .get(bookId)
      .then((b) => {
        if (active) setBook(b);
      })
      .catch(() => {
        if (active) setBook(null);
      });
    return () => {
      active = false;
    };
  }, [streaming, bookId]);

  async function startQuiz() {
    // In streaming mode `bookId` is the one-shot stream token, not the book's
    // public id; the completed book (set on book_complete) carries the real id.
    const quizBookId = book?.id ?? bookId;
    try {
      const qs = await booksApi.questions(quizBookId);
      setQuestions(qs);
      setShowQuiz(true);
    } catch {
      setQuestions([]);
      setShowQuiz(true);
    }
  }

  if (showQuiz && questions && activeChild) {
    return (
      <QuizFlow
        childId={activeChild.id}
        bookId={book?.id ?? bookId}
        questions={questions}
        pages={book ? toStreamingPages(book) : []}
        startingLevel={activeChild.readingLevel}
        onRequestHarderBook={() => navigate("/create")}
        onComplete={() => navigate("/")}
      />
    );
  }

  return (
    <div style={{ display: "grid", gap: "1.5rem" }}>
      <BookReader
        streamUrl={streaming ? booksApi.streamUrl(bookId) : null}
        {...(book ? { initialPages: toStreamingPages(book) } : {})}
        {...(book?.title ? { title: book.title } : {})}
        onBookComplete={setBook}
      />
      <button
        type="button"
        className="kid-button kid-button--primary"
        onClick={() => void startQuiz()}
      >
        📝 Answer some questions
      </button>
    </div>
  );
}
